import { createPortal } from 'react-dom'
import type { ReactElement, ReactNode } from 'react'
import { OverlayContext, useOverlayState } from './useOverlay'
import { Toast } from './Toast'
import { Modal } from './Modal'
import { Drawer } from './Drawer'
import type { OverlayEntry } from './useOverlay'

// ── OverlayProvider ───────────────────────────────────────────────────────────
// Mount ONCE in App.tsx (commit 5). Provides overlay context to all children
// and renders the single portal root where Toast/Modal/Drawer appear.

export interface OverlayProviderProps {
  children: ReactNode
}

export function OverlayProvider({ children }: OverlayProviderProps): ReactElement {
  const ctx = useOverlayState()
  // Scroll-lock is managed inside useOverlayState via useEffect on stack.length.

  return (
    <OverlayContext.Provider value={ctx}>
      {children}
      <OverlayPortal stack={ctx.stack} />
    </OverlayContext.Provider>
  )
}

// ── OverlayPortal ─────────────────────────────────────────────────────────────
// Single portal target rendered into document.body. Renders all stack entries
// in LIFO order — last push = topmost on screen.

interface OverlayPortalProps {
  stack: OverlayEntry[]
}

function OverlayPortal({ stack }: OverlayPortalProps): ReactElement {
  return createPortal(
    <div id="overlay-root" aria-live="off">
      {stack.map((entry) => renderEntry(entry))}
    </div>,
    document.body,
  )
}

function renderEntry(entry: OverlayEntry): ReactElement {
  const { id, onClose, payload } = entry

  switch (payload.kind) {
    case 'toast':
      return (
        <Toast
          key={id}
          variant={payload.variant}
          text={payload.text}
          onClose={onClose}
        />
      )

    case 'modal':
      return (
        <Modal
          key={id}
          open={true}
          onClose={onClose}
          title={payload.title}
        >
          {payload.children}
        </Modal>
      )

    case 'drawer':
      return (
        <Drawer
          key={id}
          open={true}
          onClose={onClose}
          title={payload.title}
        >
          {payload.children}
        </Drawer>
      )
  }
}
